import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { Turnstile } from '@marsidev/react-turnstile';

import {
    getCsrfToken,
    submitRegistration,
    getAdditionalEventPlaces
} from "../../utils/api_requests.ts";
import baseConfig from "../../config/baseConfig.js";
import { useDocumentModal } from "../DocumentModalContext/DocumentModalContext";

import "./Registration.css"

function RegistrationForm({ registrationType = 'notMember' }) {
    const { openModal } = useDocumentModal();
    const [message, setMessage] = useState('');
    const [messageVariant, setMessageVariant] = useState('');
    const [loading, setLoading] = useState(false);
    const [csrfToken, setCsrfToken] = useState('');
    const [backendError, setBackendError] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState('');
    const turnstileRef = useRef(null);
    const formRef = useRef(null);

    const [emailMismatch, setEmailMismatch] = useState(false);

    const [availablePlaces, setAvailablePlaces] = useState(null);
    const [placesError, setPlacesError] = useState(false);
    const [placesLoading, setPlacesLoading] = useState(true);

    useEffect(() => {
        getAdditionalEventPlaces()
            .then((response) => {
                if (response.success && response.data?.data) {
                    setAvailablePlaces(response.data.data.available);
                } else {
                    setPlacesError(true);
                }
            })
            .catch(() => setPlacesError(true))
            .finally(() => setPlacesLoading(false));
    }, []);

    const getInitialFormData = () => ({
        registrationType: registrationType,
        firstName: '',
        lastName: '',
        email: '',
        emailRepeat: '',
        adultsNumber: 1,
        childrenNumber: 0,
        childrenSelections: {},
        childrenAgeRanges: {
            "1": 0, // 0-3 lat
            "2": 0, // 4-7 lat
            "3": 0, // 8-15 lat
            "4": 0, // 15+ lat
        },
        additionalEventInterested: false,
        additionalEventWaitingList: false,
        adultsNumberAdditionalEvent: 0,
        childrenNumberAdditionalEvent: 0,
        childrenSelectionsAdditionalEvent: {},
        childrenAgeRangesAdditionalEvent: {
            "1": 0, // 2-10 lat
            "2": 0, // 0-2 lat / Sonda / PEG
        },
        statuteAccepted: true,
        rodoAndPolicyAccepted: true,
        advertismentedAccepted: false,
        participantType: 'Rodzic'
    });

    const [formData, setFormData] = useState(getInitialFormData);

    // Fetch CSRF token on component mount
    useEffect(() => {
        const fetchCsrfToken = async () => {
            const response = await getCsrfToken();

            if (response.success && response.data) {
                setCsrfToken(response.data.token);
                setBackendError(false);
            } else {
                console.error('Failed to fetch CSRF token:', response.error);
                setBackendError(true);
            }
        };

        fetchCsrfToken();
    }, []);

    useEffect(() => {
        if (formData.childrenNumber !== null) {
            setFormData(prevData => {
                // Keep only selections for existing children
                const cleanedSelections = {};
                for (let i = 1; i <= formData.childrenNumber; i++) {
                    if (prevData.childrenSelections && prevData.childrenSelections[i]) {
                        cleanedSelections[i] = prevData.childrenSelections[i];
                    }
                }

                // Recalculate counts
                const counts = { "1": 0, "2": 0, "3": 0, "4": 0 };
                Object.values(cleanedSelections).forEach(ageRange => {
                    if (ageRange && counts.hasOwnProperty(ageRange)) {
                        counts[ageRange]++;
                    }
                });

                return {
                    ...prevData,
                    childrenSelections: cleanedSelections,
                    childrenAgeRanges: counts
                };
            });
        }
    }, [formData.childrenNumber]);

    useEffect(() => {
        if (formData.childrenNumberAdditionalEvent !== null) {
            setFormData(prevData => {
                const cleanedSelections = {};
                for (let i = 1; i <= formData.childrenNumberAdditionalEvent; i++) {
                    if (prevData.childrenSelectionsAdditionalEvent && prevData.childrenSelectionsAdditionalEvent[i]) {
                        cleanedSelections[i] = prevData.childrenSelectionsAdditionalEvent[i];
                    }
                }

                const counts = { "1": 0, "2": 0 };
                Object.values(cleanedSelections).forEach(ageRange => {
                    if (ageRange && counts.hasOwnProperty(ageRange)) {
                        counts[ageRange]++;
                    }
                });

                return {
                    ...prevData,
                    childrenSelectionsAdditionalEvent: cleanedSelections,
                    childrenAgeRangesAdditionalEvent: counts
                };
            });
        }
    }, [formData.childrenNumberAdditionalEvent]);

    const setInvalidMessage = (e) => {
        const el = e.target;
        if (el.type === 'email') {
            el.setCustomValidity('Proszę wpisać prawidłowy adres e-mail');
        } else if (el.tagName === 'SELECT') {
            el.setCustomValidity('Proszę wybrać opcję z listy');
        } else if (el.type === 'checkbox') {
            el.setCustomValidity('To pole jest wymagane');
        } else {
            el.setCustomValidity('Proszę wypełnić to pole');
        }
    };

    const clearInvalidMessage = (e) => {
        e.target.setCustomValidity('');
    };

    const prepareValidationMessages = () => {
        if (!formRef.current) return;
        formRef.current.querySelectorAll('[required]').forEach((el) => {
            el.setCustomValidity('');
            if (el.validity.valid) return;
            if (el.type === 'email') {
                el.setCustomValidity('Proszę wpisać prawidłowy adres e-mail');
            } else if (el.tagName === 'SELECT') {
                el.setCustomValidity('Proszę wybrać opcję z listy');
            } else if (el.type === 'checkbox') {
                el.setCustomValidity('To pole jest wymagane');
            } else {
                el.setCustomValidity('Proszę wypełnić to pole');
            }
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Convert snake_case to camelCase
        const camelCaseName = name.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

        if (camelCaseName === 'additionalEventInterested' && !checked) {
            setFormData({
                ...formData,
                additionalEventInterested: false,
                adultsNumberAdditionalEvent: 0,
                childrenNumberAdditionalEvent: 0,
                childrenSelectionsAdditionalEvent: {},
                childrenAgeRangesAdditionalEvent: { "1": 0, "2": 0 }
            });
            return;
        }

        setFormData({
            ...formData,
            [camelCaseName]: type === 'checkbox' ? checked : value
        });
    };


    const handleParticipantTypeChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value === 'Inny' ? '' : value
        });
    };

    const handleChildrenAgeRangeChange = (e) => {
        const { name, value } = e.target;
        const childIndex = parseInt(name.split('_')[1]);

        setFormData(prevData => {
            // Create a temporary object to store individual selections
            const childrenSelections = { ...prevData.childrenSelections };
            childrenSelections[childIndex] = value;

            // Count occurrences of each age range
            const counts = { "1": 0, "2": 0, "3": 0, "4": 0 };
            Object.values(childrenSelections).forEach(ageRange => {
                if (ageRange && counts.hasOwnProperty(ageRange)) {
                    counts[ageRange]++;
                }
            });

            return {
                ...prevData,
                childrenSelections,
                childrenAgeRanges: counts
            };
        });
    };

    const handleAdditionalEventChildrenAgeRangeChange = (e) => {
        const { name, value } = e.target;
        const childIndex = parseInt(name.split('_')[3]);

        setFormData(prevData => {
            const childrenSelections = { ...prevData.childrenSelectionsAdditionalEvent };
            childrenSelections[childIndex] = value;

            const counts = { "1": 0, "2": 0 };
            Object.values(childrenSelections).forEach(ageRange => {
                if (ageRange && counts.hasOwnProperty(ageRange)) {
                    counts[ageRange]++;
                }
            });

            return {
                ...prevData,
                childrenSelectionsAdditionalEvent: childrenSelections,
                childrenAgeRangesAdditionalEvent: counts
            };
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setEmailMismatch(false);

        if (formData.email !== formData.emailRepeat) {
            console.error('Email and email repeat do not match.');
            setEmailMismatch(true);
            setMessageVariant('danger');
            return setMessage('Proszę sprawdzić podany adres email i spróbować ponownie.');
        }

        if (backendError) {
            console.error('Cannot submit form: Backend service is not available');
            setMessageVariant('danger');
            return setMessage('Coś poszło nie tak, spróbuj ponownie później. W razie dalszychproblemów skontaktuj się z organizatorem.');
        }
        if (!csrfToken) {
            console.error('Security token missing. Please refresh the page.');
            setMessageVariant('danger');
            return setMessage('Proszę odświeżyć stronę i spróbować ponownie. W razie dalszych problemów skontaktuj się z organizatorem.');
        }

        if (!baseConfig.localMode && !turnstileToken) {
            console.error('Turnstile token missing. Please complete the bot check.');
            setMessageVariant('danger');
            return setMessage('Proszę ukończyć weryfikację "Nie jestem robotem" przed wysłaniem formularza. W razie dalszych problemów skontaktuj się z organizatorem.');
        }


        // Set loading state
        setLoading(true);
        setMessage('Przetwarzanie zgłoszenia...');
        setMessageVariant('info');

        // Submit registration using API request handler (include Turnstile token in payload)
        let response = await submitRegistration(csrfToken, { ...formData, turnstileToken });

        // Handle CSRF token errors - refresh token and ask user to retry (do not auto-retry,
        // as the Turnstile token is single-use and must be re-solved by the user).
        if (!response.success && response.status === 403) {
            console.warn('Request rejected (403). Refreshing CSRF token and resetting bot check...');

            const tokenResponse = await getCsrfToken();
            if (tokenResponse.success && tokenResponse.data) {
                setCsrfToken(tokenResponse.data.token);
            }

            // Reset the Turnstile widget — each token is single-use.
            setTurnstileToken('');
            if (turnstileRef.current) {
                turnstileRef.current.reset();
            }

            setMessageVariant('warning');
            setMessage('Weryfikacja wygasła. Proszę ukończyć weryfikację ponownie i spróbować jeszcze raz.');
            setLoading(false);
            return;
        }

        if (response.status === 400) {
            setMessageVariant('danger');
            setMessage(response.message);
            setLoading(false);
            return;
        }

        // Handle response based on success/failure
        if (response.success) {
            setMessageVariant('success');
            console.log(response.message);
            setMessage('Twoje zgłoszenie zostało wysłane. Sprawdź swoją pocztę email (także w folderze "Spam"). W razie dodatkowych pytań skontaktuj się z organizatorem.');
            console.log("Registration successful!");
            setFormData(getInitialFormData());
            setEmailMismatch(false);
            setTurnstileToken('');
            if (turnstileRef.current) {
                turnstileRef.current.reset();
            }
        } else {
            // Display error message from backend
            setMessageVariant('danger');
            console.log(response.message);
            setMessage('Wystąpił błąd podczas wysyłania zgłoszenia. Proszę spróbować ponownie później. W razie dalszych problemów skontaktuj się z organizatorem.');

            // If network error (status 0), set backend error flag
            if (response.status === 0) {
                setBackendError(true);
            }

            // Log detailed error information
            console.error('Submission error:', {
                status: response.status,
                message: response.message,
                error: response.error
            });
        }
        setLoading(false);
    };

    return (
        <Card>
            <Card.Header as="h4" className="registrationFormHeader"><i className="bi bi-person-check"></i> Formularz zgłoszeniowy</Card.Header>
            <Card.Body>
                <Card.Text>
                    Rejestracja na Konferencję trwa do dnia <b>30 kwietnia 2026</b> roku.
                    <br />
                    Rejestracja na wydarzenie towarzyszące trwa do dnia <b>14 kwietnia 2026</b> roku lub do wyczerpania miejsc.
                </Card.Text>
                <Form ref={formRef} onSubmit={handleFormSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Control
                            placeholder="Imię"
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            onInvalid={setInvalidMessage}
                            onInput={clearInvalidMessage}
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Control
                            placeholder="Nazwisko"
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            onInvalid={setInvalidMessage}
                            onInput={clearInvalidMessage}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Control
                            placeholder="Email"
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            onInvalid={setInvalidMessage}
                            onInput={clearInvalidMessage}
                            isInvalid={emailMismatch}
                            required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Control
                            placeholder="Powtórz Email"
                            type="email"
                            id="emailRepeat"
                            name="emailRepeat"
                            value={formData.emailRepeat}
                            onChange={handleChange}
                            onPaste={(e) => e.preventDefault()}
                            onInvalid={setInvalidMessage}
                            onInput={clearInvalidMessage}
                            isInvalid={emailMismatch}
                            required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check
                            inline
                            type="radio"
                            id="participantTypeParent"
                            name="participantType"
                            value="Rodzic"
                            checked={formData.participantType === 'Rodzic'}
                            onChange={handleParticipantTypeChange}
                            label="Rodzic / Opiekun"
                        />
                        <Form.Check
                            inline
                            type="radio"
                            id="participantTypePerson"
                            name="participantType"
                            value="Dorosły z atrezją przełyku"
                            checked={formData.participantType === 'Dorosły z atrezją przełyku'}
                            onChange={handleParticipantTypeChange}
                            label="Dorosły z atrezją przełyku"
                        />
                        <Form.Check
                            inline
                            type="radio"
                            id="participantTypeOther"
                            name="participantType"
                            value="Inny"
                            checked={formData.participantType !== 'Rodzic' && formData.participantType !== 'Dorosły z atrezją przełyku'}
                            onChange={handleParticipantTypeChange}
                            label={
                                <span className="d-flex align-items-center gap-2">
                                    Inny/a:
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        name="participantType"
                                        value={formData.participantType !== 'Rodzic' && formData.participantType !== 'Dorosły z atrezją przełyku' ? formData.participantType : ''}
                                        onChange={handleParticipantTypeChange}
                                        placeholder="Proszę podać"
                                        style={{ width: 'auto' }}
                                        required={formData.participantType !== 'Rodzic' && formData.participantType !== 'Dorosły z atrezją przełyku'}
                                        disabled={formData.participantType === 'Rodzic' || formData.participantType === 'Dorosły z atrezją przełyku'}
                                    />
                                </span>
                            }
                        />
                    </Form.Group>
                    <Row className="flex-nowrap">
                        <Col>
                            <InputGroup className="mb-3">
                                <InputGroup.Text id="adults_form_label">
                                    Dorośli
                                </InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    id="adultsNumber"
                                    name="adultsNumber"
                                    value={formData.adultsNumber}
                                    min={1}
                                    onChange={handleChange}
                                    onInvalid={setInvalidMessage}
                                    onInput={clearInvalidMessage}
                                    aria-describedby="adults_form_label"
                                    required
                                />
                            </InputGroup>
                        </Col>
                        <Col>
                            <InputGroup className="mb-3">
                                <InputGroup.Text id="children_form_label">
                                    Dzieci
                                </InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    id="childrenNumber"
                                    name="childrenNumber"
                                    value={formData.childrenNumber}
                                    min={0}
                                    onChange={handleChange}
                                    aria-describedby="children_form_label"
                                />
                            </InputGroup>
                        </Col>
                    </Row>

                    {Array.from({ length: formData.childrenNumber }, (_, index) => (
                        <Row key={index} className="flex-nowrap mb-3">
                            <Col>
                                <Form.Label>Dziecko {index + 1}</Form.Label>
                            </Col>
                            <Col>
                                <Form.Select
                                    id={"child_" + (index + 1) + "_age_range"}
                                    name={`child_${index + 1}_age_range`}
                                    value={formData.childrenSelections?.[index + 1] || ""}
                                    onChange={handleChildrenAgeRangeChange}
                                    onInvalid={setInvalidMessage}
                                    required
                                >
                                    <option value="">Przedział wiekowy</option>
                                    <option value="1">0-3</option>
                                    <option value="2">4-7</option>
                                    <option value="3">8-15</option>
                                    <option value="4">powyżej 15</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    Wybierz przedział wiekowy.
                                </Form.Control.Feedback>
                            </Col>
                        </Row>
                    ))}

                    <InputGroup className="mb-3">
                        <Form.Check // prettier-ignore
                            type="checkbox"
                            id="additionalEventInterested"
                            name="additionalEventInterested"
                            checked={formData.additionalEventInterested}
                            onChange={handleChange}
                            label="Chcę wziąć udział w wydarzeniu towarzyszącym - wspólnej kolacji po Konferencji."
                        />
                    </InputGroup>
                    {formData.additionalEventInterested && (
                        <>
                            {(() => {
                                const requestedPlaces = parseInt(formData.adultsNumberAdditionalEvent) + parseInt(formData.childrenNumberAdditionalEvent);
                                const noPlaces = availablePlaces === 0;
                                const notEnoughPlaces = !noPlaces && requestedPlaces > availablePlaces;
                                if (!noPlaces && !notEnoughPlaces) return null;
                                return (
                                    <>
                                        <Alert variant={noPlaces ? "danger" : "warning"}>
                                            {noPlaces
                                                ? "Brak miejsc na wydarzenie towarzyszące. Można zapisać się na listę oczekujących."
                                                : "Brak wystarczającej liczby miejsc na wydarzenie towarzyszące. Można zapisać się na listę oczekujących."}
                                        </Alert>
                                        <InputGroup className="mb-3">
                                            <Form.Check // prettier-ignore
                                                type="checkbox"
                                                id="additionalEventWaitingList"
                                                name="additionalEventWaitingList"
                                                checked={formData.additionalEventWaitingList}
                                                onChange={handleChange}
                                                label="Chcę zapisać się na listę oczekujących"
                                                required
                                            />
                                        </InputGroup>
                                    </>
                                );
                            })()}

                            <Row className="flex-nowrap">
                                <Col>
                                    <InputGroup className="mb-3">
                                        <InputGroup.Text id="adults_additional_event_form_label">
                                            Dorośli i dzieci powyżej 10 roku życia
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            id="adultsNumberAdditionalEvent"
                                            name="adultsNumberAdditionalEvent"
                                            value={formData.adultsNumberAdditionalEvent}
                                            min={1}
                                            onChange={handleChange}
                                            onInvalid={setInvalidMessage}
                                            onInput={clearInvalidMessage}
                                            aria-describedby="adults_additional_event_form_label"
                                            required
                                        />
                                    </InputGroup>
                                </Col>
                                <Col>
                                    <InputGroup className="mb-3">
                                        <InputGroup.Text id="children_additional_event_form_label">
                                            Dzieci poniżej 10 roku życia
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            id="childrenNumberAdditionalEvent"
                                            name="childrenNumberAdditionalEvent"
                                            value={formData.childrenNumberAdditionalEvent}
                                            min={0}
                                            onChange={handleChange}
                                            aria-describedby="children_additional_event_form_label"
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                        </>
                    )}

                    {Array.from({ length: formData.childrenNumberAdditionalEvent }, (_, index) => (
                        <Row key={index} className="flex-nowrap mb-3">
                            <Col>
                                <Form.Label>Dziecko {index + 1}</Form.Label>
                            </Col>
                            <Col>
                                <Form.Select
                                    id={"child_additional_event_" + (index + 1) + "_age_range"}
                                    name={`child_additional_event_${index + 1}_age_range`}
                                    value={formData.childrenSelectionsAdditionalEvent?.[index + 1] || ""}
                                    onChange={handleAdditionalEventChildrenAgeRangeChange}
                                    onInvalid={setInvalidMessage}
                                    required
                                >
                                    <option value="">Przedział wiekowy</option>
                                    <option value="1">2 - 10 lat</option>
                                    <option value="2">0 - 2 lat / sonda / PEG</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    Wybierz przedział wiekowy.
                                </Form.Control.Feedback>
                            </Col>
                        </Row>
                    ))}

                    <InputGroup className="mb-3">
                        <Form.Label>
                            <strong>Zgody obowiązkowe</strong>
                        </Form.Label>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="statuteAccepted"
                            name="statuteAccepted"
                            checked={formData.statuteAccepted}
                            onChange={handleChange}
                            onInvalid={setInvalidMessage}
                            label={<>Oświadczam, że zapoznałem/am się z{' '}
                                <a href="#" onClick={(e) => { e.preventDefault(); openModal('statute'); }}>
                                    Regulaminem Konferencji
                                </a>{' '}i akceptuję jego postanowienia.</>}
                            required
                        >
                        </Form.Check>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="rodoAndPolicyAccepted"
                            name="rodoAndPolicyAccepted"
                            checked={formData.rodoAndPolicyAccepted}
                            onChange={handleChange}
                            onInvalid={setInvalidMessage}
                            label={<>Oświadczam, że zapoznałem/am się z{' '}
                                <a href="#" onClick={(e) => { e.preventDefault(); openModal('privacy'); }}>
                                    Polityką Prywatności
                                </a>{' '}oraz{' '}
                                <a href="#" onClick={(e) => { e.preventDefault(); openModal('rodo'); }}>
                                    Klauzulą Informacyjną RODO
                                </a>.</>}
                            required
                        >
                        </Form.Check>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Label>
                            <strong>Zgody opcjonalne</strong>
                        </Form.Label>
                    </InputGroup>
                    <InputGroup className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="advertismentedAccepted"
                            name="advertismentedAccepted"
                            checked={formData.advertismentedAccepted}
                            onChange={handleChange}
                            className="sm-3"
                            label="Wyrażam zgodę na przetwarzanie moich danych osobowych (email) w celach marketingowych i promocyjnych przez Polskie Stowarzyszenie Rodzin z Atrezją Przełyku z siedzibą we Wrocławiu, ul. Piękna 60AB/17, 50-506 NIP: 8993002964, REGON: 529570950, w tym wysyłanie informacji o przyszłych konferencjach (zgodę mogę wycofać w dowolnym momencie poprzez kontakt z administratorem)."
                        >
                        </Form.Check>
                    </InputGroup>
                    {baseConfig.localMode ? (
                        null
                    ) :
                        (
                            <InputGroup className="mb-3 justify-content-center">
                                <Turnstile
                                    ref={turnstileRef}
                                    siteKey={baseConfig.turnstileSiteKey}
                                    onSuccess={(token) => setTurnstileToken(token)}
                                    onExpire={() => setTurnstileToken('')}
                                    onError={() => setTurnstileToken('')}
                                />
                            </InputGroup>
                        )
                    }
                    {message && (
                        <InputGroup className="mb-3">
                            <Alert variant={messageVariant}>
                                {message}
                            </Alert>
                        </InputGroup>
                    )}

                    <Button
                        type="submit"
                        variant="primary"
                        className="w-100, submitButton"
                        disabled={(!baseConfig.localMode && !turnstileToken) || loading}
                        onClick={prepareValidationMessages}
                    >
                        Zapisz się teraz!
                    </Button>
                </Form>
            </Card.Body>
        </Card>
    );
}

export default RegistrationForm;
